import React, { useState, useEffect } from "react";
import EditNutritionModal from "../../../ui/nutrition/EditNutritionModal";
import { MdOutlineEdit, MdAdd } from "react-icons/md";
import { fetchPresetNutrition, modifyTargetNutrients } from "../../../core/api/dietApi";
import { formatNutrientValue } from "../../../core/nutrients/formatNutrientValue";

const NutrientSummaryCard = ({
  eventId,
  profileId,
  setToastMessage,
  setToastType,
  totals = {},
  settings = null,
  onSettingsSaved,
  showEditButton = true,
  subtitle = null,
  compact = false,
  title = "Nutrition Information",
  titleTooltip = null,
  activePlanBadge = null,
  legendLabels = { met: "Met", exceeds: "Exceeds", under: "Under" },
  isTotalsLoading = false,
}) => {
  const [nutritionData, setNutritionData] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
    sugar: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(false);

  // 🔑 transform API keys → UI keys
  const _transformSettingsKeys = (apiResponse) => ({
    calories: apiResponse.calories_kcal ?? 0,
    protein: apiResponse.protein_grams ?? 0,
    carbs: apiResponse.carbs_grams ?? 0,
    fats: apiResponse.fats_grams ?? 0,
    fiber: apiResponse.fiber_grams ?? 0,
    sugar: apiResponse.sugar_grams ?? 0,
  });

  // Parent-provided settings win (single fetch per page load happens in the
  // owning hook); the card only fetches itself on pages that don't pass them.
  useEffect(() => {
    if (settings) {
      setNutritionData(settings);
      setIsNewEntry(false);
      return;
    }

    const fetchSettings = async () => {
      const response = await fetchPresetNutrition(eventId, profileId);

      if (response && Object.keys(response).length > 0) {
        setNutritionData(response);
        setIsNewEntry(false);
      } else {
        setIsNewEntry(true);
      }
    };

    if (eventId && profileId) fetchSettings();
  }, [settings, eventId, profileId]);

  const handleSubmit = async (updatedValues) => {
    const requiredFields = [
      "calories",
      "protein",
      "carbs",
      "fats",
      "fiber",
      "sugar",
    ];

    const hasInvalid = requiredFields.some(
      (field) =>
        updatedValues[field] === null ||
        updatedValues[field] === "" ||
        isNaN(updatedValues[field])
    );

    if (hasInvalid) {
      setToastMessage(
        "Please fill in all nutrition fields with valid numbers."
      );
      setToastType("error");
      setTimeout(() => setToastMessage(""), 3000);
      return;
    }

    const payload = {
      event_type_id: eventId,
      operation: "ADD",
      assigned_to_id: profileId,
      calories_kcal: parseFloat(updatedValues.calories),
      protein_grams: parseFloat(updatedValues.protein),
      carbs_grams: parseFloat(updatedValues.carbs),
      fats_grams: parseFloat(updatedValues.fats),
      fiber_grams: parseFloat(updatedValues.fiber),
      sugar_grams: parseFloat(updatedValues.sugar),
    };

    const response = await modifyTargetNutrients(payload);

    if (response?.success) {
      setNutritionData(updatedValues);
      setIsNewEntry(false);
      onSettingsSaved?.(updatedValues);
      setToastMessage("Nutrition values updated successfully!");
      setToastType("success");
    } else {
      setToastMessage("Error updating nutrition values.");
      setToastType("error");
    }

    setTimeout(() => setToastMessage(""), 3000);
    setIsModalOpen(false);
  };

  const getDisplayColor = (key, settingVal, actualVal) => {
    if (!actualVal || actualVal === 0) return "text-gray-400";
    if (actualVal < settingVal) return "text-yellow-600";
    if (actualVal > settingVal) return "text-error-red-dark";
    return "text-green";
  };

  const getActualValue = (key) => {
    // Totals may use "protein"/"sugar" (singular) or "proteins"/"sugars" (plural)
    let actual = totals?.[key];
    if (actual === undefined && key === "protein") actual = totals?.proteins;
    if (actual === undefined && key === "sugar") actual = totals?.sugars;

    if (actual === undefined || actual === null || isNaN(actual)) {
      return 0;
    }

    return parseFloat(actual);
  };

  // Settings key order varies by source (API returns fats before carbs) — pin a
  // canonical reading order and tolerate singular/plural spellings.
  const compactTiles = [
    { keys: ["calories"], label: "CALORIES", unit: "kcal" },
    { keys: ["proteins", "protein"], label: "PROTEIN", unit: "g" },
    { keys: ["carbs"], label: "CARBS", unit: "g" },
    { keys: ["fats"], label: "FATS", unit: "g" },
    { keys: ["fiber"], label: "FIBER", unit: "g" },
    { keys: ["sugars", "sugar"], label: "SUGAR", unit: "g" },
  ]
    .map((tile) => {
      const key = tile.keys.find((k) => nutritionData[k] !== undefined);
      return key ? { ...tile, key, goal: nutritionData[key] } : null;
    })
    .filter(Boolean);

  // Same card as the Tracked tab, scaled down to sit inline in the toolbar.
  if (compact) {
    return (
      <div className="bg-light-orange rounded-lg shadow-md p-3 shrink-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
          <h2
            className="text-sm font-bold text-gray-800 whitespace-nowrap min-w-0"
            title={titleTooltip || undefined}
          >
            {title}
            {subtitle ? (
              <span className="ml-1.5 text-xs font-medium text-gray-500">
                {subtitle}
              </span>
            ) : null}
            {activePlanBadge}
          </h2>
          <div className="text-[10px] flex gap-2 whitespace-nowrap shrink-0">
            <span className="text-green font-semibold">● {legendLabels.met}</span>
            <span className="text-error-red-dark font-semibold">● {legendLabels.exceeds}</span>
            <span className="text-yellow-600 font-semibold">● {legendLabels.under}</span>
          </div>
          <button
            className="ml-auto p-1 rounded-full hover:bg-gray-200 shrink-0"
            onClick={() => setIsModalOpen(true)}
            aria-label={isNewEntry ? "Add nutrition goals" : "Edit nutrition goals"}
          >
            {isNewEntry ? (
              <MdAdd size={16} className="text-gray-400 hover:text-dark-orange" />
            ) : (
              showEditButton && (
                <MdOutlineEdit size={14} className="text-gray-400 hover:text-dark-orange" />
              )
            )}
          </button>
        </div>

        <div className="flex gap-2">
          {compactTiles.map(({ key, label, unit, goal }) => {
            const actual = getActualValue(key);
            const color = getDisplayColor(key, goal, actual);

            return (
              <div
                key={key}
                className="px-2 py-1.5 bg-white rounded-md shadow flex flex-col items-center justify-center min-w-[64px]"
              >
                <h5 className={`text-sm font-bold leading-tight ${color}`}>
                  {isTotalsLoading ? (
                    <span className="inline-block h-4 w-10 bg-gray-200 rounded animate-pulse" aria-hidden="true" />
                  ) : (
                    <>{formatNutrientValue(actual)} {unit}</>
                  )}
                </h5>
                <span className="text-[10px] leading-tight text-gray-500">
                  Goal: {formatNutrientValue(goal)} {unit}
                </span>
                <div className="text-[10px] leading-tight text-gray-700 mt-0.5">
                  {label}
                </div>
              </div>
            );
          })}
        </div>

        <EditNutritionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          initialValues={nutritionData}
        />
      </div>
    );
  }

  return (
    <div className="bg-light-orange w-full p-6 rounded-lg shadow-md">
      <div className="flex flex-wrap justify-between items-center gap-y-2 mb-4">
        <h2
          className="text-xl font-bold text-gray-800 min-w-0"
          title={titleTooltip || undefined}
        >
          {title}
          {subtitle ? (
            <span className="ml-2 text-sm font-medium text-gray-500">
              {subtitle}
            </span>
          ) : null}
          {activePlanBadge}
        </h2>
        <div className="text-sm mt-2 flex gap-4 shrink-0">
          <span className="text-green font-semibold">● {legendLabels.met}</span>
          <span className="text-error-red-dark font-semibold">● {legendLabels.exceeds}</span>
          <span className="text-yellow-600 font-semibold">● {legendLabels.under}</span>
        </div>
        <button
          className="p-2 rounded-full hover:bg-gray-200 shrink-0"
          onClick={() => setIsModalOpen(true)}
          aria-label={isNewEntry ? "Add nutrition goals" : "Edit nutrition goals"}
        >
          {isNewEntry ? (
            <MdAdd size={24} className="text-gray-400 hover:text-dark-orange" />
          ) : (
            showEditButton && (
              <MdOutlineEdit
                size={20}
                className="text-gray-400 hover:text-dark-orange"
              />
            )
          )}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(nutritionData).map(([key, settingVal]) => {
          const actual = getActualValue(key);
          const color = getDisplayColor(key, settingVal, actual);

          return (
            <div
              key={key}
              className="p-4 bg-white rounded-lg shadow flex flex-col items-center justify-center"
            >
              <h5 className={`text-xl font-bold ${color}`}>
                {isTotalsLoading ? (
                  <span className="inline-block h-6 w-16 bg-gray-200 rounded animate-pulse" aria-hidden="true" />
                ) : (
                  <>{formatNutrientValue(actual)} {key === "calories" ? "kcal" : "g"}</>
                )}
              </h5>

              <span className="text-xs text-gray-500">
                Goal: {formatNutrientValue(settingVal)} {key === "calories" ? "kcal" : "g"}
              </span>

              <div className="text-sm text-gray-700 mt-1">
                {key.toUpperCase()}
              </div>
            </div>
          );
        })}
      </div>

      <EditNutritionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        initialValues={nutritionData}
      />
    </div>
  );
};

export default NutrientSummaryCard;
